import { LitElement, css, html, nothing, svg, type TemplateResult } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { applyAccentVars, baseStyles, showAsTopLayer } from '../../theme/theme';

const ICON_CARET = svg`<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>`;
const ICON_CHECK = svg`<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>`;

/**
 * A filter control rendered as a pill that opens a modal list of options
 * (instead of a native `<select>`). Emits `zipper-filter-change` with
 * `{ value }` ('' = the "all" option). Inherits the embed `--zui-*` theme.
 */
@customElement('zipper-filter-pill')
export class ZipperFilterPill extends LitElement {
  static styles = [
    baseStyles,
    css`
      :host {
        display: inline-block;
      }
      .pill {
        appearance: none;
        display: inline-flex;
        align-items: center;
        gap: 0.4rem;
        border: 1px solid var(--zui-line);
        /* X2: "square" flattens the filter controls too; rounded/pill/unset
           keep the pill shape via the var() fallback. */
        border-radius: var(--zui-square-radius, 999px);
        background: #fff;
        color: var(--zui-panel-ink);
        font: inherit;
        font-size: 0.85rem;
        padding: 0.5rem 0.85rem;
        cursor: pointer;
        white-space: nowrap;
      }
      .pill.active {
        background: var(--zui-accent-soft);
        border-color: var(--zui-accent);
        color: var(--zui-accent-deep, var(--zui-accent));
        font-weight: 600;
      }
      .pill .chev {
        display: inline-flex;
        color: var(--zui-panel-ink-soft);
      }
      .overlay {
        position: fixed;
        inset: 0;
        z-index: 2147483000;
        background: rgba(20, 24, 28, 0.45);
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 1rem;
        /* SC6: this overlay is promoted to the top layer via popover="manual",
           so neutralize the UA [popover] defaults (fit-content sizing, auto
           margins, solid border) and let it fill the viewport as a backdrop. */
        width: auto;
        height: auto;
        max-width: none;
        max-height: none;
        margin: 0;
        border: 0;
        overflow: visible;
      }
      .sheet {
        background: #fff;
        color: var(--zui-headline);
        border-radius: 16px;
        width: 100%;
        max-width: 360px;
        max-height: 70vh;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        box-shadow: 0 24px 60px -20px rgba(0, 0, 0, 0.45);
      }
      .sheet-head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 1rem 1.125rem 0.75rem;
        font-family: var(--zui-font);
        font-weight: 700;
        font-size: 1rem;
        color: var(--zui-headline);
        border-bottom: 1px solid var(--zui-line);
      }
      .close {
        appearance: none;
        background: none;
        border: 0;
        font: inherit;
        font-size: 1.2rem;
        line-height: 1;
        color: var(--zui-panel-ink-soft);
        cursor: pointer;
        padding: 0 0.25rem;
      }
      .opts {
        overflow-y: auto;
        padding: 0.375rem;
      }
      .opt {
        appearance: none;
        width: 100%;
        text-align: left;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 0.5rem;
        background: none;
        border: 0;
        border-radius: 10px;
        font: inherit;
        font-size: 0.95rem;
        color: var(--zui-headline);
        padding: 0.7rem 0.75rem;
        cursor: pointer;
      }
      .opt:hover {
        background: var(--zui-panel);
      }
      .opt.sel {
        color: var(--zui-accent-deep, var(--zui-accent));
        font-weight: 700;
      }
      /* Mobile: dock as a bottom sheet. */
      @media (max-width: 540px) {
        .overlay {
          align-items: flex-end;
          padding: 0;
        }
        .sheet {
          max-width: none;
          max-height: 80vh;
          border-radius: 18px 18px 0 0;
        }
      }
    `,
  ];

  @property({ type: String }) value = '';
  @property({ type: String }) label = 'All';
  @property({ attribute: false }) options: string[] = [];
  /** N1: the embed's accent, threaded down from the parent schedule. Applied
   * inline so the pill's active state resolves the REAL accent instead of the
   * fixed teal `baseStyles` `:host { --zui-accent: … }` default — which, being
   * declared on this element's own shadow host, otherwise shadows the value it
   * would inherit from the parent embed (the active-filter border read blue). */
  @property({ type: String }) accent = '';
  // Purely presentational visibility belongs to Lit, not a domain actor.
  @state() private open = false;
  @query('.pill') private trigger?: HTMLButtonElement;

  // SC6: once the option modal is rendered, lift it into the browser top layer
  // so its fixed backdrop centers in the viewport rather than the (possibly
  // very tall) embed. Removing the node on close pops it off the top layer.
  updated(): void {
    // N1: push accent/-deep/-soft onto this host inline so they beat the
    // baseStyles `:host` default and drive the active pill's border/bg/text.
    applyAccentVars(this, this.accent);
    if (this.open) {
      showAsTopLayer(this.renderRoot.querySelector('.overlay'));
    }
  }

  private close(): void {
    this.open = false;
    void this.updateComplete.then(() => {
      this.trigger?.focus();
      // Closing removes a native popover from the top layer. Chromium performs
      // its own focus restoration at the end of that rendering turn, so
      // reinforce the host-trigger contract on the following frame.
      globalThis.requestAnimationFrame?.(() => this.trigger?.focus());
    });
  }

  private choose(v: string): void {
    this.close();
    this.dispatchEvent(
      new CustomEvent('zipper-filter-change', {
        detail: { value: v },
        bubbles: true,
        composed: true,
      }),
    );
  }

  render(): TemplateResult {
    return html`
      <button
        class="pill ${this.value ? 'active' : ''}"
        type="button"
        @click=${() => (this.open = true)}
      >
        <span>${this.value || this.label}</span>
        <span class="chev">${ICON_CARET}</span>
      </button>
      ${this.open
        ? html`<div
            class="overlay"
            popover="manual"
            @keydown=${(event: KeyboardEvent) => {
              if (event.key === 'Escape') {
                event.preventDefault();
                event.stopPropagation();
                this.close();
              }
            }}
            @click=${(e: Event) => {
              if (e.target === e.currentTarget) this.close();
            }}
          >
            <div class="sheet" role="dialog" aria-modal="true">
              <div class="sheet-head">
                <span>${this.label}</span>
                <button
                  class="close"
                  aria-label="Close"
                  @click=${() => this.close()}
                >
                  ✕
                </button>
              </div>
              <div class="opts">
                <button
                  class="opt ${this.value ? '' : 'sel'}"
                  @click=${() => this.choose('')}
                >
                  <span>${this.label}</span>
                  ${this.value ? nothing : ICON_CHECK}
                </button>
                ${this.options.map(
                  (o) =>
                    html`<button
                      class="opt ${o === this.value ? 'sel' : ''}"
                      @click=${() => this.choose(o)}
                    >
                      <span>${o}</span>
                      ${o === this.value ? ICON_CHECK : nothing}
                    </button>`,
                )}
              </div>
            </div>
          </div>`
        : nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'zipper-filter-pill': ZipperFilterPill;
  }
}
